import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

/**
 * Standalone Preview Page
 * Uses WordPress mshots for fast screenshot + live widget overlay
 */
const StandalonePreview: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams] = useSearchParams();
  const websiteUrl = searchParams.get('url');
  const [isLoading, setIsLoading] = useState(true);
  const [screenshotFailed, setScreenshotFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const widgetLoadedRef = useRef(false);
  const maxRetries = 2;

  const widgetUrl = useMemo(() => `${window.location.origin}/widget/widget-loader.js`, []);

  // WordPress mshots - fast, free, reliable
  const screenshotUrl = useMemo(() => {
    if (!websiteUrl) return null;
    const base = `https://s.wordpress.com/mshots/v1/${encodeURIComponent(websiteUrl)}?w=1920`;
    return retryCount > 0 ? `${base}&_t=${Date.now()}` : base;
  }, [websiteUrl, retryCount]);

  // Load live widget overlay
  useEffect(() => {
    if (!clientId || widgetLoadedRef.current) return;
    widgetLoadedRef.current = true;

    const script = document.createElement('script');
    script.src = widgetUrl;
    script.async = true;
    script.setAttribute('data-client-id', clientId);
    script.setAttribute('data-brndsh-widget', 'true');
    document.body.appendChild(script);

    return () => {
      const s = document.querySelector('script[data-brndsh-widget="true"]');
      if (s) s.remove();
      const c = document.getElementById('ai-chat-widget-container');
      if (c) c.remove();
    };
  }, [clientId, widgetUrl]);

  const handleScreenshotLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;

    // Check for broken/tiny images
    if (img.naturalWidth < 100 || img.naturalHeight < 100) {
      setScreenshotFailed(true);
      setIsLoading(false);
      return;
    }

    // WordPress placeholder is 400x300 - retry
    if (img.naturalWidth === 400 && img.naturalHeight === 300) {
      if (retryCount < maxRetries) {
        setTimeout(() => setRetryCount(prev => prev + 1), 1500);
        return;
      }
    }

    setIsLoading(false);
  };

  const handleScreenshotError = () => {
    setScreenshotFailed(true);
    setIsLoading(false);
  };

  if (!clientId || !websiteUrl) {
    return (
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: 'white' }}>
        Missing client ID or website URL
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', background: '#fff', overflow: 'hidden' }}>
      {/* Loading overlay */}
      {isLoading && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: 'white', zIndex: 10 }}>
          <div style={{ width: '60px', height: '60px', position: 'relative', marginBottom: '24px' }}>
            <div style={{ position: 'absolute', inset: 0, border: '3px solid rgba(59, 130, 246, 0.2)', borderRadius: '50%' }} />
            <div style={{ position: 'absolute', inset: 0, border: '3px solid transparent', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }`}</style>
          </div>
          <div style={{ fontSize: '18px', fontWeight: 500, marginBottom: '8px' }}>
            {retryCount > 0 ? `Generating Preview (${retryCount}/${maxRetries})...` : 'Loading Preview...'}
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', maxWidth: '300px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{websiteUrl}</div>
          <div style={{ display: 'flex', gap: '6px', marginTop: '20px' }}>
            {[0, 1, 2].map((i) => <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6', animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite` }} />)}
          </div>
        </div>
      )}

      {/* Screenshot background + live widget */}
      {!screenshotFailed && screenshotUrl && (
        <>
          {/* Hidden img for load detection */}
          <img
            key={retryCount}
            src={screenshotUrl}
            alt=""
            style={{ display: 'none' }}
            onLoad={handleScreenshotLoad}
            onError={handleScreenshotError}
          />
          {/* Full viewport background */}
          {!isLoading && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${screenshotUrl})`,
                backgroundSize: '100% 100%',
                backgroundPosition: 'top left',
                backgroundRepeat: 'no-repeat',
                zIndex: 1
              }}
            />
          )}
        </>
      )}

      {/* Fallback UI */}
      {screenshotFailed && (
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, opacity: 0.1, backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
          <div style={{ textAlign: 'center', color: 'white', zIndex: 2, padding: '40px' }}>
            <div style={{ width: '80px', height: '80px', margin: '0 auto 24px', borderRadius: '50%', background: 'rgba(59, 130, 246, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '12px' }}>Widget Preview</h2>
            <p style={{ fontSize: '14px', color: '#94a3b8', marginBottom: '8px', maxWidth: '400px' }}>
              Preview unavailable for this site.<br />The widget is live and fully functional!
            </p>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(0,0,0,0.3)', padding: '10px 16px', borderRadius: '8px', marginTop: '16px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
              <span style={{ color: '#94a3b8', fontSize: '13px' }}>{websiteUrl}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StandalonePreview;
